import { createStyles, Stack, Textarea } from '@mantine/core';
import IconEmojiSelector from '@repo/common/components/IconEmojiSelector';
import { SELECTABLE_PROPERTY_OPTIONS } from '@repo/common/components/SelectableProperty/constants';
import { EntityType } from '@repo/common/enums/entityType';
import { typography } from '@repo/theme/primitives';
import type { FormikProps } from 'formik';
import { capitalize } from 'lodash-es';
import { ContentInput } from '../../../features/CreateEntityModal/components/ContentInput';
import { SelectableProperty } from '../../EntityPageLayout/EntityPropertySidebar/SelectableProperty';
import { PropertySelector } from '../../Properties/PropertySelector';
import TagPropertySelector from '../../Properties/TagPropertySelector';
import { RichEditor } from '../../RichEditor';
import { AssigneeTemplateSelector } from './AssigneeTemplateSelector';
import { CollectionsTemplateSelector } from './CollectionsTemplateSelector';
import type { TemplateFormik } from './ManageTemplatePage';
import { OwnerTemplateSelector } from './OwnerTemplateSelector';
import { RelatedResourceTemplateSelector } from './RelatedResourceTemplateSelector';
import { SynonymsTemplateSelector } from './SynonymsTemplateSelector';
import { TeamsTemplateSelector } from './TeamsTemplateSelector';

const useStyles = createStyles((theme) => ({
	textAreaRoot: {
		flex: 1,
	},
	textAreaInput: {
		fontSize: typography.text.xxl,
		fontWeight: typography.weight.bold,
		lineHeight: '32px',
		padding: 0,
		overflowY: 'hidden',

		'&:focus': {
			boxShadow: 'none',
			borderColor: 'transparent',
			backgroundColor: 'transparent',
		},

		'&:disabled': {
			opacity: 1,
			backgroundColor: theme.other.getColor('fill/transparent/default'),
			color: theme.other.getColor('text/primary/default'),
			cursor: 'text',
		},
	},
	editorWrapper: {
		'.heading-actions, .block-menu-trigger': {
			display: 'none !important',
		},
		'.heading-content::before': {
			display: 'none !important',
		},
		'h1::before, h2::before, h3::before, h4::before, h5::before, h6::before': {
			display: 'none !important',
		},
	},
}));

interface CustomizeTemplateProps {
	formik: FormikProps<TemplateFormik>;
	type: EntityType;
}

export function CustomizeTemplate({ formik, type }: CustomizeTemplateProps) {
	const { classes } = useStyles();

	const getPlaceholder = (value: string) => {
		switch (value) {
			case EntityType.glossary:
				return 'Glossary term';
			case EntityType.question:
				return 'Question';
			default:
				return `${capitalize(type)} name`;
		}
	};

	const showHeaderAndProperties = [
		EntityType.glossary,
		EntityType.document,
		EntityType.collection,
		EntityType.question,
	].includes(type);

	const showDescription = [EntityType.glossary, EntityType.question].includes(
		type
	);

	const glossaryTerm = type === EntityType.glossary;
	const question = type === EntityType.question;

	return (
		<Stack spacing="2xl">
			{showHeaderAndProperties && (
				<Stack spacing="sm">
					{!question && (
						<IconEmojiSelector
							value={formik.values.icon}
							onChange={(icon) => formik.setFieldValue('icon', icon)}
						/>
					)}
					<Stack spacing="xs">
						<Textarea
							classNames={{
								root: classes.textAreaRoot,
								input: classes.textAreaInput,
							}}
							value={formik.values.title}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							placeholder={getPlaceholder(type)}
							name="title"
							variant="unstyled"
							autosize
							size="md"
							data-testid="title-input"
							error={formik.touched.title ? formik.errors.title : undefined}
						/>
						{showDescription && (
							<Stack className={classes.editorWrapper}>
								<RichEditor
									readOnly={false}
									disableTopGap
									emptyDocPlaceholder={'Add description...'}
									onChangeCallback={(value) =>
										formik.setFieldValue('description', value)
									}
									initialValue={formik.values.description}
								/>
							</Stack>
						)}
					</Stack>
				</Stack>
			)}
			{glossaryTerm && (
				<Stack spacing="3xs">
					<SynonymsTemplateSelector formik={formik} />
					<RelatedResourceTemplateSelector formik={formik} />
				</Stack>
			)}
			{showHeaderAndProperties && (
				<Stack spacing="3xs">
					{question && (
						<SelectableProperty label="Status">
							<PropertySelector
								selected={
									formik.values.status ||
									SELECTABLE_PROPERTY_OPTIONS.status[0].value
								}
								type="single"
								value="status"
								iconType="tabler"
								isViewerUser={false}
								readOnly={false}
								options={SELECTABLE_PROPERTY_OPTIONS.status}
								onChange={(value) => void formik.setFieldValue('status', value)}
							/>
						</SelectableProperty>
					)}

					{!question && (
						<SelectableProperty label="Status">
							<PropertySelector
								selected={formik.values.published}
								type="single"
								value="published"
								iconType="tabler"
								isViewerUser={false}
								readOnly={false}
								options={SELECTABLE_PROPERTY_OPTIONS.published}
								onChange={(value) =>
									void formik.setFieldValue('published', value)
								}
							/>
						</SelectableProperty>
					)}

					{!question && (
						<SelectableProperty label="Owners">
							<OwnerTemplateSelector formik={formik} />
						</SelectableProperty>
					)}

					{question && (
						<SelectableProperty label="Assignee">
							<AssigneeTemplateSelector formik={formik} />
						</SelectableProperty>
					)}

					<SelectableProperty label="Teams" dataTestId="teams">
						<TeamsTemplateSelector
							selected={formik.values.teams ?? []}
							handleChange={(value) => formik.setFieldValue('teams', value)}
							allowEmpty={true}
						/>
					</SelectableProperty>

					<SelectableProperty label="Collections" dataTestId="collections">
						<CollectionsTemplateSelector formik={formik} />
					</SelectableProperty>

					<SelectableProperty label="Tags">
						<TagPropertySelector
							initialValue={formik.values.tags}
							readOnly={false}
							placeholder={'Add tag'}
							placeholderIconName="tag"
							forceVariant="tertiary"
							forceSourceTags={false}
							entityIntegrationId={undefined}
							onChange={(value) => formik.setFieldValue('tags', value)}
						/>
					</SelectableProperty>

					{!glossaryTerm && <RelatedResourceTemplateSelector formik={formik} />}
				</Stack>
			)}
			<Stack>
				<ContentInput
					initialValue={formik.values.definition}
					onChangeCallback={(value) =>
						formik.setFieldValue('definition', value)
					}
					emptyDocPlaceholder={'Start writing or press “/” for actions...'}
					template
				/>
			</Stack>
		</Stack>
	);
}
